import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Heading,
  Text,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { cricketModelService, FullModelRequest, FullModelResponse } from '../../services/cricketModelService';

export const FullModelForm: React.FC = () => {
  const [ballsBowledA, setBallsBowledA] = useState<number>(0);
  const [runsA, setRunsA] = useState<number>(0);
  const [wktsA, setWktsA] = useState<number>(0);
  const [ballsBowledB, setBallsBowledB] = useState<number>(0);
  const [runsB, setRunsB] = useState<number>(0);
  const [wktsB, setWktsB] = useState<number>(0);
  const [aFoursSoFar, setAFoursSoFar] = useState<number>(0);
  const [aSixesSoFar, setASixesSoFar] = useState<number>(0);
  const [bFoursSoFar, setBFoursSoFar] = useState<number>(0);
  const [bSixesSoFar, setBSixesSoFar] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<FullModelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const request: FullModelRequest = {
        ballsBowledA,
        runsA,
        wktsA,
        ballsBowledB,
        runsB,
        wktsB,
        aFoursSoFar,
        aSixesSoFar,
        bFoursSoFar,
        bSixesSoFar,
      };
      
      const response = await cricketModelService.getFullModel(request);
      setResult(response);
      
      toast({
        title: 'Success',
        description: `Model calculated in ${response.durationMillis}ms`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      setError('Failed to calculate model. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to calculate model',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Heading size="md" mb={4}>Full Cricket Model</Heading>
      <Text mb={4}>
        Calculate win probabilities and runs distributions for both teams.
      </Text>
      
      <Box as="form" onSubmit={handleSubmit}>
        <VStack spacing={3} align="stretch">
          <Box>
            <Heading size="xs" mb={2}>Team A (First Innings)</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <FormControl isRequired>
                <FormLabel>Balls Bowled</FormLabel>
                <NumberInput min={0} max={120} value={ballsBowledA} onChange={(_, value) => setBallsBowledA(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Runs</FormLabel>
                <NumberInput min={0} value={runsA} onChange={(_, value) => setRunsA(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Wickets</FormLabel>
                <NumberInput min={0} max={10} value={wktsA} onChange={(_, value) => setWktsA(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Fours</FormLabel>
                <NumberInput min={0} value={aFoursSoFar} onChange={(_, value) => setAFoursSoFar(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Sixes</FormLabel>
                <NumberInput min={0} value={aSixesSoFar} onChange={(_, value) => setASixesSoFar(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          </Box>

          <Box>
            <Heading size="xs" mb={2}>Team B (Second Innings)</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Balls Bowled</FormLabel>
                <NumberInput min={0} max={120} value={ballsBowledB} onChange={(_, value) => setBallsBowledB(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Runs</FormLabel>
                <NumberInput min={0} value={runsB} onChange={(_, value) => setRunsB(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Wickets</FormLabel>
                <NumberInput min={0} max={10} value={wktsB} onChange={(_, value) => setWktsB(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Fours</FormLabel>
                <NumberInput min={0} value={bFoursSoFar} onChange={(_, value) => setBFoursSoFar(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel fontSize="sm">Sixes</FormLabel>
                <NumberInput min={0} value={bSixesSoFar} onChange={(_, value) => setBSixesSoFar(value)}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </SimpleGrid>
          </Box>
          
          <Button type="submit" colorScheme="blue" isLoading={isLoading}>
            Calculate Model
          </Button>
        </VStack>
      </Box>
      
      {error && (
        <Text color="red.500" mt={4}>
          {error}
        </Text>
      )}
      
      {result && result.value && (
        <VStack spacing={3} mt={4} align="stretch">
          <Text fontSize="xs" color="gray.600" fontWeight="bold">Calculated in {result.durationMillis}ms</Text>
          
          <Box p={2} borderWidth={1} borderRadius="md" bg="blue.50">
            <Heading size="xs" mb={2}>Win Probabilities</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              <Box>
                <Text fontSize="2xs" fontWeight="bold" mb={1} color="gray.600">2-Way (No Draw)</Text>
                <SimpleGrid columns={2} spacing={2}>
                  <Box>
                    <Text fontSize="2xs">Team A</Text>
                    <Text fontSize="md" fontWeight="bold" color={result.value.twoWay.teamAProbability > result.value.twoWay.teamBProbability ? "green.600" : "red.600"}>{(result.value.twoWay.teamAProbability * 100).toFixed(2)}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="2xs">Team B</Text>
                    <Text fontSize="md" fontWeight="bold" color={result.value.twoWay.teamBProbability > result.value.twoWay.teamAProbability ? "green.600" : "red.600"}>{(result.value.twoWay.teamBProbability * 100).toFixed(2)}%</Text>
                  </Box>
                </SimpleGrid>
              </Box>
              
              <Box>
                <Text fontSize="2xs" fontWeight="bold" mb={1} color="gray.600">3-Way (With Draw)</Text>
                <SimpleGrid columns={3} spacing={1}>
                  <Box>
                    <Text fontSize="2xs">Team A</Text>
                    <Text fontSize="sm" fontWeight="bold" color={result.value.threeWay.teamAProbability > result.value.threeWay.teamBProbability ? "green.600" : "red.600"}>{(result.value.threeWay.teamAProbability * 100).toFixed(2)}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="2xs">Draw</Text>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">{(result.value.threeWay.drawProbability * 100).toFixed(2)}%</Text>
                  </Box>
                  <Box>
                    <Text fontSize="2xs">Team B</Text>
                    <Text fontSize="sm" fontWeight="bold" color={result.value.threeWay.teamBProbability > result.value.threeWay.teamAProbability ? "green.600" : "red.600"}>{(result.value.threeWay.teamBProbability * 100).toFixed(2)}%</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </SimpleGrid>
          </Box>

          <Box p={2} borderWidth={1} borderRadius="md" bg="purple.50">
            <Heading size="xs" mb={2}>Over/Under Lines - Runs</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Team A</Text>
                <Text fontSize="sm" fontWeight="bold" color="purple.600">{result.value.teamATotalRuns.line.toFixed(1)}</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.teamATotalRuns.overProbability > result.value.teamATotalRuns.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamATotalRuns.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamATotalRuns.underProbability > result.value.teamATotalRuns.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamATotalRuns.underProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
              
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Team B</Text>
                <Text fontSize="sm" fontWeight="bold" color="purple.600">{result.value.teamBTotalRuns.line.toFixed(1)}</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.teamBTotalRuns.overProbability > result.value.teamBTotalRuns.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamBTotalRuns.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamBTotalRuns.underProbability > result.value.teamBTotalRuns.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamBTotalRuns.underProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
              
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Match</Text>
                <Text fontSize="sm" fontWeight="bold" color="purple.600">{result.value.matchTotalRuns.line.toFixed(1)}</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.matchTotalRuns.overProbability > result.value.matchTotalRuns.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.matchTotalRuns.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.matchTotalRuns.underProbability > result.value.matchTotalRuns.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.matchTotalRuns.underProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
            </SimpleGrid>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
            <Box p={2} borderWidth={1} borderRadius="md" bg="orange.50">
              <Heading size="xs" mb={2}>Over/Under Lines - 4s</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Team A</Text>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">{result.value.teamATotal4s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.teamATotal4s.overProbability > result.value.teamATotal4s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamATotal4s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamATotal4s.underProbability > result.value.teamATotal4s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamATotal4s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
                
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Team B</Text>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">{result.value.teamBTotal4s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.teamBTotal4s.overProbability > result.value.teamBTotal4s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamBTotal4s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamBTotal4s.underProbability > result.value.teamBTotal4s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamBTotal4s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
                
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Match</Text>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600">{result.value.matchTotal4s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.matchTotal4s.overProbability > result.value.matchTotal4s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.matchTotal4s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.matchTotal4s.underProbability > result.value.matchTotal4s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.matchTotal4s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
              </SimpleGrid>
            </Box>

            <Box p={2} borderWidth={1} borderRadius="md" bg="red.50">
              <Heading size="xs" mb={2}>Over/Under Lines - 6s</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Team A</Text>
                  <Text fontSize="sm" fontWeight="bold" color="red.600">{result.value.teamATotal6s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.teamATotal6s.overProbability > result.value.teamATotal6s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamATotal6s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamATotal6s.underProbability > result.value.teamATotal6s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamATotal6s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
                
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Team B</Text>
                  <Text fontSize="sm" fontWeight="bold" color="red.600">{result.value.teamBTotal6s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.teamBTotal6s.overProbability > result.value.teamBTotal6s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.teamBTotal6s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamBTotal6s.underProbability > result.value.teamBTotal6s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.teamBTotal6s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
                
                <Box p={2} bg="white" borderRadius="md">
                  <Text fontWeight="bold" fontSize="2xs" mb={1}>Match</Text>
                  <Text fontSize="sm" fontWeight="bold" color="red.600">{result.value.matchTotal6s.line.toFixed(1)}</Text>
                  <Text fontSize="xs"><Text as="span" color={result.value.matchTotal6s.overProbability > result.value.matchTotal6s.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.matchTotal6s.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.matchTotal6s.underProbability > result.value.matchTotal6s.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.matchTotal6s.underProbability * 100).toFixed(1)}%</Text></Text>
                </Box>
              </SimpleGrid>
            </Box>
          </SimpleGrid>

          <Box p={2} borderWidth={1} borderRadius="md" bg="teal.50">
            <Heading size="xs" mb={2}>Match Wickets</Heading>
            <Box p={2} bg="white" borderRadius="md">
              <Text fontSize="sm" fontWeight="bold" color="teal.600">Line: {result.value.matchTotalWickets.line.toFixed(1)}</Text>
              <Text fontSize="xs" mt={1}><Text as="span" color={result.value.matchTotalWickets.overProbability > result.value.matchTotalWickets.underProbability ? "green.600" : "red.600"} fontWeight="bold">Over: {(result.value.matchTotalWickets.overProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.matchTotalWickets.underProbability > result.value.matchTotalWickets.overProbability ? "green.600" : "red.600"} fontWeight="bold">Under: {(result.value.matchTotalWickets.underProbability * 100).toFixed(1)}%</Text></Text>
            </Box>
          </Box>

          <Box p={2} borderWidth={1} borderRadius="md" bg="cyan.50">
            <Heading size="xs" mb={2}>Odd/Even - Runs</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Team A</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.teamATotalRunsOddEven.oddProbability > result.value.teamATotalRunsOddEven.evenProbability ? "green.600" : "red.600"} fontWeight="bold">Odd: {(result.value.teamATotalRunsOddEven.oddProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamATotalRunsOddEven.evenProbability > result.value.teamATotalRunsOddEven.oddProbability ? "green.600" : "red.600"} fontWeight="bold">Even: {(result.value.teamATotalRunsOddEven.evenProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
              
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Team B</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.teamBTotalRunsOddEven.oddProbability > result.value.teamBTotalRunsOddEven.evenProbability ? "green.600" : "red.600"} fontWeight="bold">Odd: {(result.value.teamBTotalRunsOddEven.oddProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.teamBTotalRunsOddEven.evenProbability > result.value.teamBTotalRunsOddEven.oddProbability ? "green.600" : "red.600"} fontWeight="bold">Even: {(result.value.teamBTotalRunsOddEven.evenProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
              
              <Box p={2} bg="white" borderRadius="md">
                <Text fontWeight="bold" fontSize="2xs" mb={1}>Match</Text>
                <Text fontSize="xs"><Text as="span" color={result.value.matchTotalRunsOddEven.oddProbability > result.value.matchTotalRunsOddEven.evenProbability ? "green.600" : "red.600"} fontWeight="bold">Odd: {(result.value.matchTotalRunsOddEven.oddProbability * 100).toFixed(1)}%</Text> | <Text as="span" color={result.value.matchTotalRunsOddEven.evenProbability > result.value.matchTotalRunsOddEven.oddProbability ? "green.600" : "red.600"} fontWeight="bold">Even: {(result.value.matchTotalRunsOddEven.evenProbability * 100).toFixed(1)}%</Text></Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      )}
    </Box>
  );
};
